// VPC IPAM pricing — ONE module, imported by the enable wizard AND the Settings
// "Tier" tab, which are the only two screens that can put a tenant on the paid
// tier.
//
// Access Analyzer states the rule — "a price that disagrees with itself across
// two screens is a support ticket at best" — but implements it as two copies of
// the same constants (AccessAnalyzerSetup.js:49-50, AccessAnalyzerSettingsPage
// .js:41-42), which is exactly the shape that drifts. One module imported twice
// enforces the rule instead of restating it, and costs nothing.
//
// The rate is carried WITH ITS FETCH DATE. This is the number on the most
// consequential screen in the capability — the one a customer reads before
// agreeing to a bill they cannot opt out of — and a rate that has quietly gone
// stale misquotes that bill with no visible symptom. A price with no date is a
// price no one can check.

// --- The rate, and where it came from -------------------------------------
// Advanced tier bills per ACTIVE IP per hour, across everything the resource
// discoveries find — including addresses that sit in no IPAM pool at all.
export const IPAM_ADVANCED_HOURLY_RATE_USD = 0.00027;

// Read from aws.amazon.com/vpc/pricing on this date. Re-check it before quoting
// the figure anywhere new, and update BOTH lines together.
export const IPAM_PRICING_FETCHED_ON = '2026-08-15';
export const IPAM_PRICING_SOURCE_URL = 'https://aws.amazon.com/vpc/pricing/';

// AWS's own convention for converting an hourly rate to a monthly one.
export const HOURS_PER_MONTH = 730;

// ≈ $0.197 per active IP per month.
export const IPAM_ADVANCED_MONTHLY_RATE_USD = IPAM_ADVANCED_HOURLY_RATE_USD * HOURS_PER_MONTH;

// --- The strings both screens must show identically -----------------------
export const IPAM_ADVANCED_PRICE = '$0.00027 per active IP per hour';
export const IPAM_ADVANCED_PRICE_MONTHLY = 'about $0.20 per active IP per month';

export const IPAM_PRICE_NOTE =
  `AWS list price, taken from ${IPAM_PRICING_SOURCE_URL} on ${IPAM_PRICING_FETCHED_ON}. ` +
  'The meter covers every active IP in the accounts and Regions you enrol, whether or not ' +
  'it sits in an IPAM pool. Check the VPC pricing page for your Regions before budgeting.';

// Why the paid tier is not a choice: the Free tier refuses the cross-account
// association outright, so the inventory, overlap detection and utilization this
// capability exists for are Advanced-only.
export const IPAM_ADVANCED_REQUIRED_NOTE =
  `VPC IPAM Advanced tier is required, and it is billed — ${IPAM_ADVANCED_PRICE}. ` +
  'There is no free multi-account IPAM. AWS refuses to associate a second account’s ' +
  'resource discovery on the Free tier, so the cross-account inventory, overlap detection ' +
  'and utilization this capability exists for are Advanced-only.';

// What the Free tier actually is — stated precisely, because "there is a free
// tier" is the single most likely misreading of the alert above.
export const IPAM_FREE_TIER_NOTE =
  'The Free tier covers a single Region in a single account and includes Public IP Insights. ' +
  'It cannot manage a second account, which is the whole product here.';

// --- Estimation -----------------------------------------------------------

// estimateMonthlyCostUsd returns null — never 0 — when the active-IP count is
// unknown. An un-estimated bill is not a free one, and "$0.00" is the one
// rendering that would make an unavoidable charge look like no charge at all.
// A genuine count of 0 still returns 0: absent and empty are different answers.
export function estimateMonthlyCostUsd(activeIps) {
  if (activeIps === null || activeIps === undefined || activeIps === '') return null;
  const n = Number(activeIps);
  if (!Number.isFinite(n) || n < 0) return null;
  return n * IPAM_ADVANCED_MONTHLY_RATE_USD;
}

// formatUsd returns null for a value there is no way to price, so a caller
// cannot accidentally render a placeholder as a dollar figure.
export function formatUsd(value, fractionDigits = 2) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

// The Dashboard and the wizard both quote a whole-dollar monthly figure; the
// cents on a four-figure estimate are noise and imply a precision Prism's
// lower-bound ENI count does not have.
export function formatMonthlyCostUsd(activeIps) {
  const cost = estimateMonthlyCostUsd(activeIps);
  if (cost === null) return null;
  return formatUsd(cost, cost < 100 ? 2 : 0);
}
